import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ScrollText, Loader2, RefreshCw } from 'lucide-react';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function CreditTopupReportTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/credits/admin-topup/report');
      setEntries(data?.entries || []);
    } catch (e) { toast.error('Could not load report'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const total = entries.reduce((s, e) => s + (e.credits || 0), 0);

  return (
    <div className="max-w-5xl" data-testid="credit-topup-report-tab">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <ScrollText className="h-5 w-5 text-primary" /> Credit top-up report
          </h2>
          <p className="text-sm text-slate-600 mt-2">Audit of all backend credit grants — who added what, to which org, and when.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={load} data-testid="topup-report-refresh">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-6 mb-4 text-sm">
        <div><span className="text-slate-500">Total grants:</span> <b>{entries.length}</b></div>
        <div><span className="text-slate-500">Total credits:</span> <b>{total.toLocaleString()}</b></div>
      </div>

      <div className="bg-white border border-slate-200" data-testid="topup-report-table">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>
        ) : entries.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-400" data-testid="topup-report-empty">No credit top-ups recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Organization</th>
                <th className="text-right px-4 py-3 font-semibold">Credits</th>
                <th className="text-left px-4 py-3 font-semibold">Added by</th>
                <th className="text-left px-4 py-3 font-semibold">Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0" data-testid={`topup-report-row-${e.id}`}>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(e.created_at)}</td>
                  <td className="px-4 py-3 font-medium">{e.org_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {e.credits}
                    {e.is_owner_grant && <span className="ml-1 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-700 px-1 py-0.5">owner</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{e.actor_email || e.actor_name || e.actor_id}</td>
                  <td className="px-4 py-3 text-slate-500">{e.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
